import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { DemoResetResult, DemoStatus } from '../api/types';
import { IconAlert, IconRefresh } from '../components/icons';
import { Pill } from '../components/license';
import {
  Button,
  Callout,
  CheckLine,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  SecretReveal,
  SkeletonRows,
  useToast,
} from '../components/ui';
import { useCapabilities } from '../lib/auth';

/**
 * Resetting the demo fixtures.
 *
 * The point of this page is confidence: you should be able to break the demo
 * companies freely — expire them, revoke licenses, delete users — and put them
 * back in one click. So the page's job is to make unmistakably clear what will
 * and will not be touched.
 */
export function DemoDataPage() {
  const can = useCapabilities();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [reissueSecret, setReissueSecret] = useState(false);
  const [result, setResult] = useState<DemoResetResult | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['demo-status'],
    queryFn: () => api.get<DemoStatus>('/api/staff/demo/status'),
  });

  const reset = useMutation({
    mutationFn: () =>
      api.post<DemoResetResult>('/api/staff/demo/reset', {
        confirm: 'reset demo data',
        reissueProductSecret: reissueSecret,
      }),
    onSuccess: (data) => {
      // Everything on screen elsewhere is now stale.
      void queryClient.invalidateQueries();
      setResult(data);
      setConfirming(false);
      push({
        tone: 'success',
        title: 'Demo data reset',
        body: data.product.secretUnchanged
          ? 'Acme is licensed for a year again, and Stale Corp is expired again.'
          : 'Fixtures rebuilt, and Product A has a new key and secret — copy them below.',
      });
    },
  });

  const enabled = data?.enabled ?? false;
  const isSuperadmin = can.isAdmin;
  // Anything the operator made themselves — the part of a reset that hurts.
  const ownCount =
    (data?.companies.filter((company) => !company.isDemo).length ?? 0) +
    (data?.otherProducts ?? 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Demo data</h1>
          <p className="page-head__sub">
            Reset puts the whole database back to exactly what the seed produces. Anything created
            since — companies, credentials, licenses, products — is deleted.
          </p>
        </div>
        <div className="page-head__actions">
          <Button
            variant="danger"
            icon={<IconRefresh />}
            disabled={!enabled || !isSuperadmin || isPending}
            title={
              !enabled
                ? 'Disabled on this deployment (DEMO_RESET_ENABLED is not set)'
                : !isSuperadmin
                  ? 'Resetting demo data requires an admin account'
                  : undefined
            }
            onClick={() => {
              setReissueSecret(false);
              setConfirming(true);
            }}
          >
            Reset demo data
          </Button>
        </div>
      </div>

      {isPending && (
        <section className="panel">
          <SkeletonRows rows={3} />
        </section>
      )}

      {isError && <ErrorNote error={error} />}

      {data && (
        <>
          {!enabled && (
            <div style={{ marginBottom: 'var(--s5)' }}>
              <Callout tone="warn" title="Reset is disabled here">
                This deployment runs with <code className="mono">DEMO_RESET_ENABLED</code> unset in
                production, so the fixtures cannot be rebuilt from the console. Set it to{' '}
                <code className="mono">true</code> if this is a demo environment.
              </Callout>
            </div>
          )}

          {enabled && !isSuperadmin && (
            <div style={{ marginBottom: 'var(--s5)' }}>
              <Callout tone="info" title="Superadmin only">
                Your role cannot reset demo data. Resetting deletes records, so it is restricted to
                admins.
              </Callout>
            </div>
          )}

          <section className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">What reset deletes</h2>
                <p className="panel__hint">
                  Everything. The two demo companies come back with the same ids they always have,
                  so anything a product has keyed on them keeps working.
                </p>
              </div>
            </div>

            {ownCount > 0 && (
              <div className="panel__body" style={{ paddingBottom: 0 }}>
                <Callout tone="danger" title={`${ownCount} of these are not demo fixtures`}>
                  You created them after the seed, and a reset deletes them permanently — there is
                  no undo. Only the two demo companies come back.
                </Callout>
              </div>
            )}

            {data.companies.length === 0 ? (
              <EmptyState
                icon={<IconAlert size={30} />}
                title="No demo fixtures present"
                body="Nothing here yet. Running reset now will create the two demo companies from scratch."
              />
            ) : (
              <div className="table-wrap scroll-area">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Code</th>
                      <th>Credentials</th>
                      <th>Licenses</th>
                      <th>Effect of reset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.companies.map((company) => (
                      <tr key={company.code}>
                        <td className="cell-strong">{company.name}</td>
                        <td className="cell-mono">{company.code}</td>
                        <td className="table__num">{company.users}</td>
                        <td className="table__num">{company.licenses}</td>
                        <td>
                          {company.isDemo ? (
                            <Pill tone="expiring">Rebuilt, same ids</Pill>
                          ) : (
                            /* Yours. It does not come back. */
                            <Pill tone="expired">Deleted for good</Pill>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td className="cell-strong">Product A</td>
                      <td className="cell-mono">{data.productCode}</td>
                      <td className="cell-meta">—</td>
                      <td className="cell-meta">—</td>
                      <td>
                        <Pill tone="live">Kept, secret unchanged</Pill>
                      </td>
                    </tr>
                    {data.otherProducts > 0 && (
                      <tr>
                        <td className="cell-strong">
                          {data.otherProducts} other product
                          {data.otherProducts === 1 ? '' : 's'}
                        </td>
                        <td className="cell-meta">—</td>
                        <td className="cell-meta">—</td>
                        <td className="cell-meta">—</td>
                        <td>
                          <Pill tone="expired">Deleted for good</Pill>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="panel">
            <div className="panel__head">
              <h2 className="panel__title">After a reset</h2>
            </div>
            <div className="panel__body stack">
              <dl className="dl">
                <dt>Acme Manufacturing</dt>
                <dd>
                  License valid for one year ·{' '}
                  <code className="mono">acme.admin</code> / <code className="mono">AcmeAdmin123!</code>{' '}
                  (admin) · <code className="mono">acme.bob</code> /{' '}
                  <code className="mono">AcmeMember123!</code>
                </dd>
                <dt>Stale Corp</dt>
                <dd>
                  License <strong>expired yesterday</strong> ·{' '}
                  <code className="mono">stale.admin</code> /{' '}
                  <code className="mono">AcmeAdmin123!</code> ·{' '}
                  <code className="mono">stale.member</code> /{' '}
                  <code className="mono">AcmeMember123!</code>
                </dd>
                <dt>Also gone</dt>
                <dd>
                  Every other company, credential, license and product · all activity and email
                  history. There is no undo.
                </dd>
                <dt>Kept</dt>
                <dd>
                  Staff accounts, so you stay signed in · the Product A API key and secret, so a
                  configured product keeps working · the fixtures’ ids, which never change
                </dd>
              </dl>

              <Callout tone="info" title="Why Stale Corp is expired on purpose">
                It is the behaviour this service exists to provide: an expired license locks out the
                company administrator <em>and</em> every user that administrator created. Try
                signing in as either from <Link to="/licenses">Licenses</Link> — both must be
                refused with <code className="mono">LICENSE_EXPIRED</code>.
              </Callout>
            </div>
          </section>

          {result && (
            <section className="panel">
              <div className="panel__head">
                <div>
                  <h2 className="panel__title">Last reset</h2>
                  <p className="panel__hint">
                    Removed {result.deleted.companies} companies, {result.deleted.users}{' '}
                    credentials, {result.deleted.licenses} licenses, {result.deleted.auditEntries}{' '}
                    activity entries and {result.deleted.emails} sent emails, then rebuilt the
                    fixtures.
                  </p>
                </div>
              </div>
              <div className="panel__body stack">
                {result.product.secretUnchanged ? (
                  <Callout tone="info" title="Product A credentials unchanged">
                    The API key and secret were left as they are, so whatever you have pointed at
                    Heron keeps working. Key: <code className="mono">{result.product.apiKey}</code>
                  </Callout>
                ) : (
                  <>
                    <Callout tone="danger" title="Update every product using this key">
                      Product A has a new key and secret, and the previous pair stopped working the
                      moment you confirmed. Until you paste these into each product’s{' '}
                      <code className="mono">HERON_PRODUCT_KEY</code> /{' '}
                      <code className="mono">HERON_PRODUCT_SECRET</code> and restart it, every
                      sign-in through that product fails with{' '}
                      <code className="mono">PRODUCT_CREDENTIALS_INVALID</code> — which products are
                      told never to show a user, so it will look like an unexplained outage.
                      Copy them now; only the hash is stored.
                    </Callout>
                    <SecretReveal label="X-Heron-Product-Key" value={result.product.apiKey} />
                    {result.product.apiSecret && (
                      <SecretReveal
                        label="X-Heron-Product-Secret"
                        value={result.product.apiSecret}
                      />
                    )}
                  </>
                )}
              </div>
            </section>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => reset.mutate()}
        title="Reset the demo data?"
        body={
          <>
            Every company, credential, license and product in this database is deleted, and the
            seed fixtures are put back. That includes anything you created yourself. Activity and
            email history go too, and everyone signed in is signed out.
            <br />
            <br />
            Your staff account survives, so you stay signed in to this console.{' '}
            {reissueSecret
              ? 'Product A gets a new API key and secret, shown once on the next screen.'
              : 'Product A keeps its API key and secret.'}
          </>
        }
        confirmLabel="Reset demo data"
        confirmWord="reset"
        loading={reset.isPending}
        error={reset.error}
        extra={
          <CheckLine
            title="Also issue a new API key and secret for Product A"
            description={
              reissueSecret
                ? 'The current key and secret stop working immediately — anything already configured with them will fail to authenticate until you paste the new pair in.'
                : 'Leave this off to keep Product A working. Turn it on if the secret was lost or rotated: only a hash is stored, so this is the only way back to a usable pair.'
            }
            checked={reissueSecret}
            onChange={setReissueSecret}
          />
        }
      />
    </>
  );
}
